import {ProductTileV2 as ProductTileV2Type} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_v2.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  ImageTrackingData,
  RecoListTrackingData,
  getGalleryImageTrackingData,
  useTrackWishlist,
} from '@aboutyou/tracking';
import {LoadingSpinner, useShowToast} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useState} from 'react';
import {MarkOptional} from 'ts-essentials';
import {useBadgesListKeys} from '../../../features/BadgeList/useBadgesList';
import {
  ProductTile,
  ProductTileProps,
} from '../../../features/ProductTile/ProductTile';
import {AddToBasketPayload} from '../../../features/ProductTile/types';
import {RemoveItemButton} from './RemoveItemButton';
import {RemoveItemConfirmModal} from './RemoveItemConfirmModal';

export interface WishlistAddToBasketPayload {
  productId: number;
  brandName: string;
  badgesListKeys: string[];
  couponPromotionId?: string;
  imageTrackingData?: ImageTrackingData;
}

interface WishlistProductTileProps {
  id?: string;
  'data-skip-link'?: string;
  gridColumns?: number;
  tile: ProductTileV2Type;
  importance?: 'high' | 'low' | 'auto';
  streamPosition: number;
  trackingData?: MarkOptional<RecoListTrackingData, 'sectionIndex'> & {
    sortingChannel?: string;
  };
  onClick?: (ev: React.MouseEvent) => void;
  showConfirmOnDeleteItem?: boolean;
  onClickAddToBasket: (payload: WishlistAddToBasketPayload) => void;
  smallAddToBasketCtaText: boolean;
  onSoldoutCtaClick: (id: number) => void;
  ProductTileComponent?: React.FC<
    Omit<ProductTileProps, 'ProductTileComponent'>
  >;
  enableImageGalleryAnimation?: boolean;
}

export const WishlistProductTile: React.FC<WishlistProductTileProps> = ({
  id,
  'data-skip-link': dataSkipLink,
  tile,
  streamPosition,
  trackingData,
  gridColumns,
  showConfirmOnDeleteItem,
  smallAddToBasketCtaText,
  onClickAddToBasket,
  enableImageGalleryAnimation,
  ProductTileComponent = ProductTile,
}) => {
  const badgesListKeys = useBadgesListKeys({
    badges: tile.badges,
    sale: null,
    campaignSale: null,
    useDefaultLabels: true,
    showPriceOverhaulV2SaleLabel: false,
  });

  const tracker = useTrackWishlist({
    product: tile.productTracker,
    price: tile.priceV2,
    brand: tile.brandTracker,
    streamPosition,
    trackingData,
    badges: badgesListKeys,
    getImageTrackingData: () => getGalleryImageTrackingData(tile.gallery, 0),
  });

  const {t} = useTranslation();
  const showToast = useShowToast();
  const [isSpinnerVisible, setIsSpinnerVisible] = useState(false);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);

  const handleClickRemoveItemButton = () => setIsSpinnerVisible(true);
  const handleClickRemoveItemSuccess = () => {
    tracker('removed-from-wishlist');
  };

  const handleClickRemoveItemError = () => {
    setIsSpinnerVisible(false);
    setIsConfirmModalVisible(false);

    showToast({
      content: t('Oops! Something went wrong!'),
      type: 'ERROR',
      'data-testid': 'RemoveFromWishlistButtonToast',
    });
  };

  const handleAddToBasket = (payload: AddToBasketPayload) => {
    onClickAddToBasket({
      productId: payload.product.id,
      brandName: tile.brandTracker.name,
      couponPromotionId: tile.couponPromotionId,
      badgesListKeys: payload.badgesListKeys ?? [],
      imageTrackingData: payload.imageTrackingData,
    });
  };

  const handleColorClick = (colorProductId: number) => {
    onClickAddToBasket({
      productId: colorProductId,
      brandName: tile.brandTracker.name,
      couponPromotionId: tile.couponPromotionId,
      badgesListKeys,
    });
  };

  return (
    <WishlistTileWrapper id={id} data-skip-link={dataSkipLink}>
      <ProductTileComponent
        tile={tile}
        streamPosition={streamPosition}
        trackingData={trackingData}
        gridColumns={gridColumns}
        enableImageGalleryAnimation={enableImageGalleryAnimation}
        productTileGallery
        onClickAddCta={handleAddToBasket}
        onColorClick={handleColorClick}
        addCtaLabel={smallAddToBasketCtaText ? t('Add') : t('Add to basket')}
        headerAction={
          <RemoveItemButton
            iconType="trash"
            productId={tile.productId}
            onClickRemoveItem={handleClickRemoveItemButton}
            onClickRemoveItemSuccess={handleClickRemoveItemSuccess}
            onClickRemoveItemError={handleClickRemoveItemError}
            onShowConfirm={
              showConfirmOnDeleteItem
                ? () => setIsConfirmModalVisible(true)
                : undefined
            }
          />
        }
      />
      {isSpinnerVisible && !showConfirmOnDeleteItem && (
        <FullSizeSpinner
          data-testid="WishlistTileFullSizeSpinner"
          data-visible="true"
        >
          <LoadingSpinner />
        </FullSizeSpinner>
      )}
      <RemoveItemConfirmModal
        isOpen={isConfirmModalVisible}
        onClose={() => setIsConfirmModalVisible(false)}
        onClickConfirm={handleClickRemoveItemButton}
        onClickRemoveItemSuccess={handleClickRemoveItemSuccess}
        onClickRemoveItemError={handleClickRemoveItemError}
        isLoading={isSpinnerVisible}
        productId={tile.productId}
      />
    </WishlistTileWrapper>
  );
};

const WishlistTileWrapper = styled.div`
  position: relative;
`;

const FullSizeSpinner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: none;
  pointer-events: none;
  display: flex;
  justify-content: center;
  z-index: -1;
  opacity: 0;
  transition: opacity 300ms ease;

  [data-visible='true'] {
    opacity: 1;
    background: rgba(255, 255, 255, 0.5);
    pointer-events: auto;
    z-index: 1;
  }
`;
